/**
 * Affiliate link beacons + floating YouTube screens.
 * Each beacon has a unique shape. Approach to reveal a soft glass popup.
 */
import { MeshBuilder }      from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Vector3, Matrix } from '@babylonjs/core/Maths/math';
import { GlowLayer }        from '@babylonjs/core/Layers/glowLayer';

// ── Affiliate data ────────────────────────────────────────────────────────────
export const AFFILIATES = [
    { emoji:'📖', name:'ספרון הדרכה חינמי',   desc:'מאפס ועד מנויים — מדריך דוגמנית AI מלא',     url:'https://drive.google.com/file/d/1aUNHyH6-he4Kd-pjuB25LmElWN5itPr5/view',             color:'#ff9f43', x:12,  z:5,   shape:0 },
    { emoji:'💎', name:'Fanvue',               desc:'פלטפורמת דוגמניות AI. עמלה 80% ליוצר',       url:'https://www.fanvue.com/signup?referral=FV-WICEPP',                                    color:'#ff4da6', x:-12, z:5,   shape:1 },
    { emoji:'🖥️', name:'VPS Server',           desc:'שרת חזק וזול. הפעל בוטים ואתרים 24/7',       url:'https://vps-server.host/aff.php?aff=1',                                               color:'#54a0ff', x:18,  z:-5,  shape:2 },
    { emoji:'🤖', name:'Claude Code',          desc:'$10 טוקנים מתנה בהרשמה דרך הקישור',         url:'https://claude.ai/referral/ua5D4j-j7g',                                               color:'#ff9f43', x:-18, z:-5,  shape:3 },
    { emoji:'🎙️', name:'ElevenLabs',           desc:'שכפול קול בעברית. יצירת פודקאסט ווידאו',    url:'https://try.elevenlabs.io/ng53v2x6zt1e',                                              color:'#a29bfe', x:8,   z:18,  shape:4 },
    { emoji:'🎬', name:'KIE.AI',               desc:'Sora, Runway, Kling — כל הוידאו AI בזול',   url:'https://kie.ai/?ref=3327c4a36bf83f36fdb39e5be5ec60ba',                               color:'#fd79a8', x:-8,  z:18,  shape:5 },
    { emoji:'📣', name:'Blotato',              desc:'פרסום ל-20 רשתות חברתיות אוטומטי',          url:'https://blotato.com/?ref=lior',                                                       color:'#00b894', x:20,  z:12,  shape:0 },
    { emoji:'🧠', name:'Manus',                desc:'סוכן AI עצמאי + 500 טוקנים חינם',           url:'https://manus.im/invitation/VYBM24VVQNTAUK',                                          color:'#e17055', x:-20, z:12,  shape:1 },
    { emoji:'🎨', name:'Meshy.ai',             desc:'טקסט לתלת-ממד בשניות — גיימינג ופרסום',     url:'https://www.meshy.ai/?utm_source=referral-program',                                   color:'#6c5ce7', x:15,  z:-18, shape:2 },
    { emoji:'✈️', name:'Travelpayouts',        desc:'שיווק שותפים לחברות תעופה ותיירות',          url:'https://www.travelpayouts.com/',                                                      color:'#0984e3', x:-15, z:-18, shape:3 },
    { emoji:'🏨', name:'Stay22',              desc:'רווחים פסיביים על כל הזמנת לינה',            url:'https://www.stay22.com/',                                                             color:'#00cec9', x:5,   z:-20, shape:4 },
    { emoji:'💼', name:'PartnerStack',         desc:'תוכניות שותפים SaaS — 30-50% פסיבי',        url:'https://partnerstack.com/',                                                           color:'#fdcb6e', x:-5,  z:-20, shape:5 },
    { emoji:'📈', name:'Blink',                desc:'השקעות בלי עמלות. מתחילים מ-10 שקל',        url:'https://www.blink.co/',                                                               color:'#55efc4', x:22,  z:0,   shape:0 },
    { emoji:'🌏', name:'Yesim eSIM',           desc:'SIM דיגיטלי לכל מדינה. שיווק שותפים',       url:'https://www.yesim.app/',                                                              color:'#74b9ff', x:-22, z:0,   shape:1 },
    { emoji:'🏦', name:'Payoneer',             desc:'חשבון בנק בינלאומי + $250 מתנה',             url:'https://www.payoneer.com/',                                                           color:'#ff7675', x:10,  z:-12, shape:2 },
    { emoji:'💰', name:'CashDo',              desc:'תוכנית שותפים ישראלית — 50% מהרווחים',       url:'https://cashdo.co.il/',                                                               color:'#ffeaa7', x:-10, z:-12, shape:3 },
    { emoji:'🛡️', name:'ESET',                desc:'אנטי-וירוס AI. קוד: RS15 לקבלת 15% הנחה',   url:'https://www.eset.com/',                                                               color:'#81ecec', x:0,   z:12,  shape:4 },
    { emoji:'🌙', name:'Kimi',                desc:'מודל AI סיני חזק + שנת קרדיטים מתנה',        url:'https://kimi.moonshot.cn/',                                                           color:'#c3a6ff', x:0,   z:-14, shape:5 },
    { emoji:'📊', name:'Polymarket',           desc:'שוק תחזיות עם כסף אמיתי — AI, פוליטיקה',   url:'https://polymarket.com/',                                                             color:'#6ab04c', x:25,  z:-25, shape:0 },
];

// YouTube video screens scattered in the world
const YT_SCREENS = [
    { id:'BkV3m9WjvW4', x:-30, y:4, z:5   },
    { id:'tzmCvOcbu-4', x:30,  y:4, z:-15 },
];

const POPUP_R2 = 49; // 7m²

// ── Shape builders: 6 distinct shapes ────────────────────────────────────────
function buildShape(idx, name, scene) {
    switch (idx % 6) {
        case 0: // Torus ring (floating halo)
            return MeshBuilder.CreateTorus(`aff_${name}`, {
                diameter: 0.9, thickness: 0.12, tessellation: 32,
            }, scene);
        case 1: // Octahedron (diamond)
            return MeshBuilder.CreatePolyhedron(`aff_${name}`, {
                type: 1, size: 0.38,
            }, scene);
        case 2: // Elongated box / obelisk
            return MeshBuilder.CreateBox(`aff_${name}`, {
                width: 0.22, height: 1.7, depth: 0.22,
            }, scene);
        case 3: // Icosphere
            return MeshBuilder.CreateSphere(`aff_${name}`, {
                diameter: 0.56, segments: 4, // low-poly = faceted look
            }, scene);
        case 4: // Dodecahedron
            return MeshBuilder.CreatePolyhedron(`aff_${name}`, {
                type: 2, size: 0.32,
            }, scene);
        case 5: // Torus Knot — spiral
            return MeshBuilder.CreateTorusKnot(`aff_${name}`, {
                radius: 0.28, tube: 0.06, radialSegments: 64, tubularSegments: 12,
                p: 2, q: 3,
            }, scene);
    }
}

export class AffiliateSystem {
    constructor(scene, engine, terrain) {
        this._scene      = scene;
        this._engine     = engine;
        this._terrain    = terrain;
        this._beacons    = [];
        this._activeBeacon = null;
        this._popup      = document.getElementById('affiliate-popup');
        this._screenEls  = [];

        this._glow = new GlowLayer('affGlow', scene, { blurKernelSize: 20 });
        this._glow.intensity = 1.3;

        this._buildBeacons();
        this._buildScreens();
    }

    _buildBeacons() {
        const scene   = this._scene;
        const terrain = this._terrain;

        for (const a of AFFILIATES) {
            const color    = Color3.FromHexString(a.color);
            const groundY  = terrain ? terrain.heightAt(a.x, a.z) : 0;

            // ── Thin base crystal (always cylinder, thin) ──
            const base = MeshBuilder.CreateCylinder(`affBase_${a.name}`, {
                height: 1.8, diameterTop: 0.05, diameterBottom: 0.18, tessellation: 6,
            }, scene);
            base.position.set(a.x, groundY + 0.9, a.z);
            base.renderingGroupId = 1;
            const baseMat = new StandardMaterial(`affBM_${a.name}`, scene);
            baseMat.emissiveColor = color.scale(0.55);
            baseMat.disableLighting = true;
            base.material = baseMat;

            // ── Unique top shape ──
            const top = buildShape(a.shape, a.name, scene);
            top.position.set(a.x, groundY + 2.3, a.z);
            top.renderingGroupId = 1;
            if (a.shape === 2) top.position.y = groundY + 1.9; // obelisk is tall
            const topMat = new StandardMaterial(`affTM_${a.name}`, scene);
            topMat.emissiveColor = color.clone();
            topMat.disableLighting = true;
            top.material = topMat;

            // ── Tiny ring / corona below the shape ──
            const ring = MeshBuilder.CreateTorus(`affRing_${a.name}`, {
                diameter: 0.7, thickness: 0.025, tessellation: 32,
            }, scene);
            ring.position.set(a.x, groundY + 1.85, a.z);
            ring.renderingGroupId = 1;
            const ringMat = new StandardMaterial(`affRM_${a.name}`, scene);
            ringMat.emissiveColor = color.scale(0.8);
            ringMat.disableLighting = true;
            ring.material = ringMat;

            this._glow.addIncludedOnlyMesh(base);
            this._glow.addIncludedOnlyMesh(top);
            this._glow.addIncludedOnlyMesh(ring);

            this._beacons.push({
                ...a, base, top, ring,
                baseMat, topMat, ringMat,
                groundY, color,
            });
        }
    }

    _buildScreens() {
        for (const s of YT_SCREENS) {
            const el = document.createElement('div');
            el.className = 'yt-screen';
            el.innerHTML = `<iframe src="https://www.youtube.com/embed/${s.id}?autoplay=0" frameborder="0" allowfullscreen allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"></iframe>`;
            el.style.display = 'none';
            document.body.appendChild(el);
            this._screenEls.push({ ...s, el });
        }
    }

    update(charPos, time) {
        const canvas = this._engine.getRenderingCanvas();
        const cw = canvas.clientWidth;
        const ch = canvas.clientHeight;
        const vp = { x:0, y:0, width:cw, height:ch };
        const wm = Matrix.Identity();
        const tm = this._scene.getTransformMatrix();

        let nearestBeacon = null;
        let nearestD2     = POPUP_R2;
        let nearestSP     = null;

        for (const b of this._beacons) {
            const t     = time * 1.6 + b.x * 0.25;
            const pulse = 0.7 + 0.3 * Math.sin(time * 2.2 + b.z * 0.35);

            b.top.position.y  = b.groundY + 2.3 + Math.sin(t) * 0.18;
            b.top.rotation.y  = time * (b.shape % 2 === 0 ? 0.9 : -0.7);
            b.top.rotation.x  = b.shape === 0 ? Math.PI / 2 : 0;
            if (b.shape === 5) b.top.rotation.x = time * 0.4;

            b.ring.position.y = b.groundY + 1.85 + Math.sin(t * 0.7) * 0.06;
            b.ring.rotation.y = -time * 1.1;
            b.ring.rotation.x = Math.sin(time * 0.5 + b.x) * 0.3;

            b.topMat.emissiveColor.set(b.color.r * pulse, b.color.g * pulse, b.color.b * pulse);
            b.baseMat.emissiveColor.set(b.color.r * pulse * 0.4, b.color.g * pulse * 0.4, b.color.b * pulse * 0.4);
            b.ringMat.emissiveColor.set(b.color.r * 0.6, b.color.g * 0.6, b.color.b * 0.6);

            const dx = charPos.x - b.x, dz = charPos.z - b.z;
            const d2 = dx*dx + dz*dz;
            if (d2 < nearestD2) {
                nearestD2 = d2;
                nearestBeacon = b;
                // Project the top of the beacon to screen
                const wp = new Vector3(b.x, b.groundY + 3.2, b.z);
                nearestSP = Vector3.Project(wp, wm, tm, vp);
            }
        }

        // Popup: show + position next to beacon
        if (nearestBeacon !== this._activeBeacon) {
            this._activeBeacon = nearestBeacon;
            if (nearestBeacon) this._showPopup(nearestBeacon);
            else               this._hidePopup();
        }
        // Track position every frame while shown
        if (this._activeBeacon && nearestSP && nearestSP.z > 0 && nearestSP.z < 1) {
            this._positionPopup(nearestSP.x, nearestSP.y, cw, ch);
        }

        // YouTube screens
        for (const s of this._screenEls) {
            const wp = new Vector3(s.x, s.y, s.z);
            const sp = Vector3.Project(wp, wm, tm, vp);
            if (sp.z > 0.01 && sp.z < 0.997) {
                const dist01 = Math.min(1, sp.z);
                const scale  = Math.max(0.25, 1.2 - dist01 * 1.1);
                s.el.style.display   = '';
                s.el.style.left      = `${sp.x}px`;
                s.el.style.top       = `${sp.y}px`;
                s.el.style.transform = `translate(-50%, -50%) scale(${scale})`;
                s.el.style.opacity   = Math.min(1, (0.997 - dist01) * 8).toString();
            } else {
                s.el.style.display = 'none';
            }
        }
    }

    _positionPopup(sx, sy, cw, ch) {
        if (!this._popup) return;
        const pw = 195, ph = 110; // approx popup dims
        const PAD = 12;
        // Default: above and to the right of the beacon dot
        let left = sx + 16;
        let top  = sy - ph - 10;
        // Clamp to viewport
        if (left + pw > cw - PAD) left = sx - pw - 16;
        if (left < PAD) left = PAD;
        if (top < PAD + 60) top = sy + 22; // below if too close to top
        if (top + ph > ch - PAD) top = ch - ph - PAD;
        this._popup.style.left = `${left}px`;
        this._popup.style.top  = `${top}px`;
    }

    _showPopup(b) {
        if (!this._popup) return;
        this._popup.querySelector('#aff-emoji').textContent = b.emoji;
        this._popup.querySelector('#aff-name').textContent  = b.name;
        this._popup.querySelector('#aff-desc').textContent  = b.desc;
        this._popup.querySelector('#aff-link').href = b.url;
        this._popup.style.setProperty('--aff-accent', b.color);
        this._popup.style.borderColor = b.color + '22';
        this._popup.classList.add('show');
    }

    _hidePopup() { this._popup?.classList.remove('show'); }

    getBeacons() { return this._beacons; }
}
